import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState, Knot } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { setStyle } from '../ui/dom.ts';

/** A cairn: stones stacked, narrowing. */
const CAIRN = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="13.2" rx="5.6" ry="1.8" fill="currentColor"/><ellipse cx="8" cy="10.2" rx="4.3" ry="1.6" fill="currentColor"/><ellipse cx="8" cy="7.4" rx="3.2" ry="1.4" fill="currentColor"/><ellipse cx="8" cy="4.9" rx="2.2" ry="1.2" fill="currentColor"/><path d="M10.6 7.6c1.6.6 1.8 2.6 1.4 4.4" stroke="#ff4f3a" stroke-width="1.2" fill="none" stroke-linecap="round"/></svg>';
/** A cord with knots. */
const CORD = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8h13" stroke="currentColor" stroke-width="1.4"/><circle cx="5" cy="8" r="1.9" fill="#ff4f3a"/><circle cx="10" cy="8" r="1.9" fill="#ff4f3a"/></svg>';
/** Sela's shout: a mouth and the rings going out. */
const SHOUT = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="5" cy="8" r="2" fill="currentColor"/><path d="M8.5 4.5a5 5 0 0 1 0 7M11 2.5a8 8 0 0 1 0 11" stroke="currentColor" stroke-width="1.3" fill="none" stroke-linecap="round"/></svg>';
/** A fork of the Furrow. */
const FORK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 15V8M8 8L3 2M8 8l5-6M8 8V1.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>';
/** A light base: a pale disc in a ring of stones. */
const BASE = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="9" rx="6" ry="3.4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-dasharray="1.6 1.2"/><ellipse cx="8" cy="9" rx="3.4" ry="1.8" fill="currentColor"/></svg>';
/** The Roller curled: a ball of plates. */
const ROLLER = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.5 6.5c3 1.5 8 1.5 11 0M2.5 9.5c3 1.5 8 1.5 11 0M8 2v12" stroke="currentColor" stroke-width="1.1" fill="none"/></svg>';
/** A mouth in the fog. */
const MOUTH = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 14V7a5 5 0 0 1 10 0v7" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>';

/** The knots, drawn as they are tied: a simple knot a ball, a turn a loop (two for two), a tight knot a square. */
const knotSvg = (kind: 'simple' | 'turn' | 'turn2' | 'tight'): string =>
  kind === 'simple'
    ? '<svg viewBox="0 0 14 16" aria-hidden="true"><path d="M7 0v16" stroke="#9aa3ab" stroke-width="1.4"/><circle cx="7" cy="8" r="3.6" fill="#ff4f3a"/></svg>'
    : kind === 'tight'
      ? '<svg viewBox="0 0 14 16" aria-hidden="true"><path d="M7 0v16" stroke="#9aa3ab" stroke-width="1.4"/><rect x="2.6" y="4.4" width="8.8" height="7.2" rx="1" fill="#ff4f3a"/><path d="M3 8h8" stroke="#2a0c08" stroke-width="1.2"/></svg>'
      : kind === 'turn'
        ? '<svg viewBox="0 0 14 16" aria-hidden="true"><path d="M7 0v4M7 12v4" stroke="#9aa3ab" stroke-width="1.4"/><ellipse cx="7" cy="8" rx="4.6" ry="4" fill="none" stroke="#ff4f3a" stroke-width="2"/></svg>'
        : '<svg viewBox="0 0 14 16" aria-hidden="true"><path d="M7 0v2M7 14v2" stroke="#9aa3ab" stroke-width="1.4"/><ellipse cx="7" cy="5" rx="4.4" ry="3" fill="none" stroke="#ff4f3a" stroke-width="1.8"/><ellipse cx="7" cy="11" rx="4.4" ry="3" fill="none" stroke="#ff4f3a" stroke-width="1.8"/></svg>';

/** A cord's knots in order: its rings (simple knots), its side (a turn for the sun, two for the sea), the tight knot. */
const knotsOf = (k: Knot): ('simple' | 'turn' | 'turn2' | 'tight')[] => [...Array.from({ length: k.rings }, () => 'simple' as const), k.side < 0 ? 'turn' : 'turn2', ...(k.tight ? ['tight' as const] : [])];

type Mark = { el: HTMLButtonElement; state: HTMLElement; at: number; point: THREE.Vector3; follow?: string };

/**
 * Aster's stones on screen (docs/sendas/explorador.md, chapter 1), inside a path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the knots (`cairns`): the cord being read, its knots drawn as tied (no words for what they say: the legend under
 *   it does), how many cairns to go and the false ones; over each cairn a mark (tap: go and read it) with its clue: on a
 *   plate, read, down; Aster's first one lit.
 * - the fog (`echo`): Sela's shout (ready, getting her breath), the fork of three; a mark over Sela (tap: she shouts)
 *   and one at each mouth of the fork (tap: in this way) that says only what was heard: an echo, or a wall.
 * - the fallen cairn (`fallen`): the long cord in its three parts once read, the bases left, the time on one, the
 *   Roller (coming, curling with its count, rolling, stunned); marks over the cairns and the bases.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class AsterHud {
  readonly panel: HTMLElement;
  private kind: 'cairns' | 'echo' | 'fallen';
  private v = new THREE.Vector3();
  private key = '';
  private marks: Mark[] = [];
  private cordRow = h('div.bh-row.as-cord');
  private cordKnots = h('span.as-knots');
  private cordText = h('span.al-wave');
  private legend = h('p.as-legend');
  private leftRow = h('div.bh-row.as-left');
  private leftText = h('span.al-wave');
  private shoutRow = h('div.bh-row.al-waverow.as-shout');
  private shoutText = h('span.al-wave');
  private forkRow = h('div.bh-row.as-fork');
  private forkText = h('span.al-wave');
  private baseRow = h('div.bh-row.al-waverow.as-base');
  private baseText = h('span.al-wave');
  private rollerRow = h('div.bh-row.al-waverow.as-roller');
  private rollerText = h('span.al-wave');
  private sela: { x: number; y: number } | null = null;

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.echo ? 'echo' : sr?.fallen ? 'fallen' : 'cairns';
    const rows: HTMLElement[] = [];
    if (this.kind === 'cairns' || this.kind === 'fallen') {
      this.cordRow.append(h('span.bh-ico', { html: CORD }), h('span.bh-label', {}, t('senda.aster.cord')), this.cordKnots, this.cordText);
      this.legend.textContent = t(this.kind === 'cairns' ? 'senda.aster.legend' : 'senda.aster.legendLong');
      rows.push(this.cordRow, this.legend);
    }
    if (this.kind === 'cairns') {
      this.leftRow.append(h('span.bh-ico', { html: CAIRN }), this.leftText);
      rows.push(this.leftRow);
      const r = sr?.cairns;
      r?.cairns.forEach((c, i) => this.addMark(i, simToWorld(c.x, c.y, new THREE.Vector3(), 1.3), CAIRN, 'senda.aster.cairnHint', `cairn-${i}`, c.plate ? t('senda.aster.plate') : ''));
    } else if (this.kind === 'echo') {
      this.shoutRow.append(h('span.bh-ico', { html: SHOUT }), h('span.bh-label', {}, t('senda.aster.shout')), this.shoutText);
      this.forkRow.append(h('span.bh-ico', { html: FORK }), this.forkText);
      rows.push(this.shoutRow, this.forkRow);
      this.addMark(0, new THREE.Vector3(), SHOUT, 'senda.aster.selaHint', 'sela', '', 'sela');
      // The mouths' marks, as many as a fork has at most (filled by the fork the hero is at).
      for (let i = 0; i < 4; i++) this.addMark(1 + i, new THREE.Vector3(), MOUTH, 'senda.aster.mouthHint', `mouth-${i}`, '');
    } else {
      this.baseRow.append(h('span.bh-ico', { html: BASE }), this.baseText);
      this.rollerRow.append(h('span.bh-ico', { html: ROLLER }), h('span.bh-label', {}, t('senda.aster.roller')), this.rollerText);
      rows.push(this.baseRow, this.rollerRow);
      const r = sr?.fallen;
      r?.cairns.forEach((c, i) => this.addMark(i, simToWorld(c.x, c.y, new THREE.Vector3(), 1.3), CAIRN, 'senda.aster.cairnHint', `cairn-${i}`, ''));
      r?.bases.forEach((b, i) => this.addMark((r.cairns.length ?? 0) + i, simToWorld(b.x, b.y, new THREE.Vector3(), 0.3), BASE, 'senda.aster.baseHint', `base-${i}`, ''));
    }
    this.panel = h(`div.bh.as-panel.as-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, point: THREE.Vector3, glyph: string, hint: string, test: string, state: string, follow?: string): void {
    const st = h('small.al-state.as-state', {}, state);
    const el = h(
      `button.al-stone.as-mark.as-${test.split('-')[0]}`,
      { type: 'button', 'data-test': `aster-${test}`, 'aria-label': t(hint), title: t(hint), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: glyph }),
      st,
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, state: st, at, point, ...(follow ? { follow } : {}) });
  }

  private setState(m: Mark, text: string): void {
    if (m.state.textContent === text) return;
    m.state.textContent = text;
    remeasureMark(m.el);
  }

  private setCord(knots: ('simple' | 'turn' | 'turn2' | 'tight' | 'gap')[], text: string): void {
    const key = knots.join(',');
    if (this.cordKnots.dataset.k !== key) {
      this.cordKnots.dataset.k = key;
      this.cordKnots.replaceChildren(...knots.map((k) => (k === 'gap' ? h('i.as-gap') : h(`i.as-knot.${k}`, { html: knotSvg(k) }))));
    }
    if (this.cordText.textContent !== text) this.cordText.textContent = text;
  }

  private updateCairns(s: BattleState): void {
    const r = s.rules?.senda?.cairns;
    const w = s.senda?.cairns;
    if (!r || !w) return;
    const k = w.step >= 1 ? r.knots[w.step - 1] : undefined;
    const reading = w.job >= 0 && w.held > 0;
    this.setCord(k && w.step < r.way.length ? knotsOf(k) : [], reading ? t('senda.aster.reading') : w.step === 0 ? t('senda.aster.touch') : !k || w.step >= r.way.length ? '' : '');
    this.cordRow.classList.toggle('reading', reading);
    const left = Math.max(0, r.way.length - Math.max(1, w.step));
    const lt = (left === 1 ? t('senda.aster.leftOne') : t('senda.aster.left', { n: left })) + (w.wrongs ? ` · ${t('senda.aster.falses', { n: w.wrongs })}` : '');
    if (this.leftText.textContent !== lt) this.leftText.textContent = lt;
    const key = `${w.st.join('')}|${w.step}|${w.job}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        const st = w.st[m.at] ?? 0;
        const c = r.cairns[m.at];
        this.setState(m, st === 1 ? t('senda.aster.read') : st === 2 ? t('senda.aster.down') : c?.plate ? t('senda.aster.plate') : '');
        m.el.classList.toggle('read', st === 1);
        m.el.classList.toggle('gone', st === 2 || w.done >= 0);
        m.el.classList.toggle('first', w.step === 0 && m.at === r.way[0]);
      }
    }
    for (const m of this.marks) {
      const sent = w.job === m.at;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.read)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  private updateEcho(s: BattleState): void {
    const r = s.rules?.senda?.echo;
    const w = s.senda?.echo;
    if (!r || !w) return;
    const sela = s.senda?.npcs.find((n) => n.id === 'sela');
    this.sela = sela && sela.hp > 0 ? { x: sela.x, y: sela.y } : null;
    const wait = w.shout.t >= 0 ? w.shout.t + r.cool - s.tick : 0;
    const out = w.shout.t >= 0 && s.tick < w.shout.t + Math.trunc((r.far * 2) / r.speed);
    const st = wait > 0 ? (out ? t('senda.aster.shoutOut') : t('senda.aster.shoutWait', { s: Math.max(1, Math.ceil(wait / TICK_HZ)) })) : t('senda.aster.shoutReady');
    if (this.shoutText.textContent !== st) this.shoutText.textContent = st;
    this.shoutRow.classList.toggle('warn', wait <= 0);
    const fk = w.fork >= r.forks.length ? t('senda.aster.out') : t('senda.aster.fork', { n: w.fork + 1, of: r.forks.length });
    const heard = (w.heard[w.fork] ?? 0).toString(2).split('').filter((x) => x === '1').length;
    const ft = fk + (heard ? ` · ${t('senda.aster.echoes', { n: heard })}` : '');
    if (this.forkText.textContent !== ft) this.forkText.textContent = ft;
    const f = r.forks[w.fork];
    const key = `${w.fork}|${w.heard[w.fork] ?? 0}|${w.walled[w.fork] ?? 0}|${w.job}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        if (m.at === 0) continue;
        const mo = f?.mouths[m.at - 1];
        m.el.classList.toggle('gone', !mo || w.done >= 0);
        if (!mo) continue;
        // The mouth's mark at its way in, a step out of the chamber.
        simToWorld(mo.at.x + Math.trunc((mo.at.x - (f?.at.x ?? 0)) / 2), mo.at.y + Math.trunc((mo.at.y - (f?.at.y ?? 0)) / 2), m.point, 0.6);
        const i = m.at - 1;
        const walled = !!((w.walled[w.fork] ?? 0) & (1 << i));
        const echoed = !!((w.heard[w.fork] ?? 0) & (1 << i));
        this.setState(m, walled ? t('senda.aster.wall') : echoed ? t('senda.aster.echo') : '');
        m.el.classList.toggle('echoed', echoed);
        m.el.classList.toggle('walled', walled);
        m.el.classList.toggle('sent', w.job === i);
      }
    }
    for (const m of this.marks) {
      if (m.at !== 0) continue;
      m.el.classList.toggle('gone', !this.sela || w.done >= 0);
      m.el.classList.toggle('ready', wait <= 0);
      m.el.disabled = s.result !== 'running';
    }
  }

  private updateFallen(s: BattleState): void {
    const r = s.rules?.senda?.fallen;
    const w = s.senda?.fallen;
    if (!r || !w) return;
    const read = w.read[r.long] === 1;
    const reading = w.job >= 0 && w.held > 0;
    const knots: ('simple' | 'turn' | 'turn2' | 'tight' | 'gap')[] = read
      ? [...Array.from({ length: r.cord.rings }, () => 'simple' as const), 'gap', r.cord.side < 0 ? 'turn' : 'turn2', 'gap', ...Array.from({ length: r.cord.then }, () => 'simple' as const)]
      : [];
    this.setCord(knots, reading ? t('senda.aster.reading') : read ? '' : t('senda.aster.cordNone'));
    this.cordRow.classList.toggle('reading', reading);
    const on = w.on >= 0 ? Math.max(0, r.stand - (s.tick - w.since)) : -1;
    const left = w.bases.filter((b) => b === 0).length;
    const bt = on >= 0 ? t('senda.aster.onBase', { s: Math.max(1, Math.ceil(on / TICK_HZ)) }) : t('senda.aster.bases', { n: left });
    if (this.baseText.textContent !== bt) this.baseText.textContent = bt;
    this.baseRow.classList.toggle('warn', on >= 0);
    const ro = w.roller;
    const e = ro.id ? s.enemies.find((x) => x.id === ro.id) : undefined;
    const rt = !ro.id
      ? t('senda.aster.rollerOff')
      : e && e.stun > 0
        ? t('senda.aster.rollerStun')
        : ro.mode === 1
          ? t('senda.aster.rollerCurl', { s: Math.max(1, Math.ceil((ro.t - s.tick) / TICK_HZ)) })
          : ro.mode === 2
            ? t('senda.aster.rollerRoll')
            : t('senda.aster.rollerWalk');
    if (this.rollerText.textContent !== rt) this.rollerText.textContent = rt;
    this.rollerRow.classList.toggle('warn', ro.mode === 1 || ro.mode === 2);
    this.rollerRow.classList.toggle('good', !!e && e.stun > 0);
    const key = `${w.read.join('')}|${w.bases.join('')}|${w.job}|${w.done}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        if (m.at < r.cairns.length) {
          this.setState(m, w.read[m.at] ? t('senda.aster.read') : '');
          m.el.classList.toggle('read', !!w.read[m.at]);
          m.el.classList.toggle('long', m.at === r.long);
        } else {
          const b = w.bases[m.at - r.cairns.length];
          this.setState(m, b === 1 ? t('senda.aster.cracked') : '');
          m.el.classList.toggle('cracked', b === 1);
          m.el.classList.toggle('gone', b === 1 || w.done >= 0);
        }
      }
    }
    for (const m of this.marks) {
      const sent = m.at < r.cairns.length && w.job === m.at;
      m.el.classList.toggle('sent', sent || (m.at >= r.cairns.length && w.on === m.at - r.cairns.length));
      const k = sent ? w.held / Math.max(1, r.read) : m.at >= r.cairns.length && w.on === m.at - r.cairns.length ? (s.tick - w.since) / Math.max(1, r.stand) : 0;
      m.el.style.setProperty('--k', Math.min(1, k).toFixed(2));
      m.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'cairns') this.updateCairns(s);
    else if (this.kind === 'echo') this.updateEcho(s);
    else this.updateFallen(s);
  }

  /** Every frame: the marks over their places (Sela's over her). */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      if (m.follow === 'sela' && this.sela) simToWorld(this.sela.x, this.sela.y, m.point, 2.2);
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
